import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Delete } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField
} from "@mui/material";
import { Button, Loading } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import {
  useBanks,
  useCampaigns,
  useChartOfAccounts,
  useRevenueMethods,
  useRevenueStore,
  useUpdateRevenue
} from "features/finances";
import {
  FILTER_OPERATIONS,
  MEMBER_STATES,
  useMembers,
  usePartners
} from "features/members";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { convertObjectToFormData } from "utils/convertObjectToFormData";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import * as yup from "yup";
import "yup-phone";
import { EthiopianDateField } from "../EthiopianDateField";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const InformationText = styled.p`
  font-size: 12px;
  font-weight: 500;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  flex: 1;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  flex: 1;
  background-color: transparent;
`;

const CheckboxLabel = styled.label`
  font-size: 12px;
  margin-right: 16px;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
`;

const PreviewThumbnail = styled.img`
  height: 80px;
  weight: 80px;
  object-fit: cover;
`;

const DropzoneContainer = styled.div`
  border: 1px dashed #b3b3b3;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: #3dadb7;
  }
`;

const DropzoneText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #5b5b5b;
`;

const DropzoneSubText = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: #b3b3b3;
`;

const StyledAutocomplete = styled(Autocomplete)`
  .MuiInputBase-root {
    font-size: 12px;
  }

  .MuiOutlinedInput-input {
    padding: 8px !important;
  }

  .MuiOutlinedInput-root.Mui-disabled {
    background-color: #f5f5f5 !important;
  }
  .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: #e0e0e0 !important;
  }
  .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input {
    color: rgba(0, 0, 0, 0.38) !important;
  }
`;

const revenueSchema = yup.object().shape(
  {
    chart_of_account_id: yup.string().required("Revenue type is required"),
    is_pledge: yup.boolean(),
    campaign_id: yup.string().when("is_pledge", {
      is: true,
      then: yup.string().required("Campaign is required")
    }),
    description: yup.string(),
    method: yup.string().required("Method is required"),
    receiptNumber: yup.string(),
    chequeNumber: yup.string(),
    accountHolderName: yup.string(),
    accountNumber: yup.string(),
    phoneNumber: yup.string().when("phoneNumber", {
      is: phone => !!phone,
      then: yup.string().phone("ET", true, "Phone is not valid"),
      otherwise: yup.string()
    }),
    referenceNumber: yup.string(),
    bank_id: yup.string(),
    amount: yup
      .number()
      .typeError("Amount must be a number")
      .required("Amount is required")
      .positive("Amount must be positive"),
    currency: yup.string().required("Currency is required"),
    receivedDate: yup.string().required("Received date is required"),
    cashReceivedDate: yup.string(),
    dueDate: yup.string(),
    bankName: yup.string(),
    cash_chart_of_account_id: yup.string().when("method", {
      is: "1",
      then: yup.string().required("Cash chart of account is required")
    })
  },
  [["phoneNumber", "phoneNumber"]]
);

export function UpdateRevenueModal({ data, openReveneVoidModal }) {
  const updateRevenueModalOpen = useRevenueStore(
    state => state.updateRevenueModalOpen
  );
  const closeUpdateRevenueModal = useRevenueStore(
    state => state.closeUpdateRevenueModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const revenueMethodsQuery = useRevenueMethods({
    churchId: selectedChurch?.id
  });
  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [
      { column: "account_type", operator: "in", value: ["INCOME", "EQUITY"] }
    ]
  });

  const assetsChartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [{ column: "account_type", operator: "in", value: ["ASSET"] }]
  });

  const banksQuery = useBanks({
    churchId: selectedChurch?.id
  });
  const membersQuery = useMembers({
    churchId: selectedChurch?.id,
    queries: {
      filter: {
        status: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: MEMBER_STATES.ACTIVE
          }
        ]
      }
    }
  });
  const campaignsQuery = useCampaigns({
    churchId: selectedChurch?.id
  });
  const partnersQuery = usePartners({
    churchId: selectedChurch?.id
  });
  const [isMember, setIsMember] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const updateRevenueMutation = useUpdateRevenue();
  const [attachmentToView, setAttachmentToView] = useState("");

  const onDrop = useCallback(acceptedFiles => {
    setSelectedFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "application/pdf": [".pdf"]
    },
    maxSize: 2 * 1024 * 1024, // 2 MB
    multiple: false
  });

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      chart_of_account_id: "",
      is_pledge: false,
      campaign_id: "",
      description: "",
      method: "",
      receiptNumber: "",
      chequeNumber: "",
      accountHolderName: "",
      accountNumber: "",
      phoneNumber: "",
      referenceNumber: "",
      bank_id: "",
      amount: "",
      currency: "ETB",
      receivedDate: "",
      cashReceivedDate: "",
      dueDate: "",
      bankName: "",
      cash_chart_of_account_id: ""
    },
    resolver: yupResolver(revenueSchema),
    context: { isMember }
  });

  const method = watch("method");
  const receivedDate = watch("receivedDate");
  const dueDate = watch("dueDate");
  const cashReceivedDate = watch("cashReceivedDate");

  useEffect(() => {
    if (
      data &&
      membersQuery.data &&
      partnersQuery.data &&
      chartOfAccountsQuery.data &&
      assetsChartOfAccountsQuery.data &&
      revenueMethodsQuery.data &&
      banksQuery.data &&
      campaignsQuery.data
    ) {
      const hasMembers = data.member_ids?.length > 0;
      const hasPartners = data.partner_ids?.length > 0;

      setIsMember(hasMembers);
      setValue("chart_of_account_id", data.chart_of_account_id || "");
      setValue("is_pledge", data.is_pledge || false);
      setValue("campaign_id", data.campaign_id || "");
      setValue("description", data.description || "");
      setValue("method", `${data.revenue_method_id}` || "");
      setValue("receiptNumber", data.receipt_number || "");
      setValue("chequeNumber", data.cheque_number || "");
      setValue("accountHolderName", data.account_holder_name || "");
      setValue("accountNumber", data.account_number || "");
      setValue("phoneNumber", data.phone_number || "");
      setValue(
        "referenceNumber",
        data.reference_number || data.transaction_number || ""
      );
      setValue("bank_id", data.bank_id || "");
      setValue("bankName", data.bank_name || "");
      setValue("amount", data.amount || "");
      setValue("currency", data.currency || "ETB");
      setValue(
        "receivedDate",
        data.received_date
          ? moment(data.received_date).format("YYYY-MM-DD")
          : ""
      );
      setValue(
        "cashReceivedDate",
        data.cash_received_date
          ? moment(data.cash_received_date).format("YYYY-MM-DD")
          : ""
      );
      setValue(
        "dueDate",
        data.cheque_due_date
          ? moment(data.cheque_due_date).format("YYYY-MM-DD")
          : ""
      );
      setValue("cash_chart_of_account_id", data.cash_chart_of_account_id || "");

      // Set selected members
      if (hasMembers) {
        const selectedMembers = membersQuery.data.result.filter(member =>
          data.member_ids?.map(id => String(id)).includes(String(member.id))
        );
        setSelectedMembers(selectedMembers);
      }

      // Set selected partners
      if (hasPartners) {
        const selectedPartners = partnersQuery.data.result.filter(partner =>
          data.partner_ids?.map(id => String(id)).includes(String(partner.id))
        );
        setSelectedPartners(selectedPartners);
      }
    }
  }, [
    data,
    setValue,
    membersQuery.data,
    partnersQuery.data,
    chartOfAccountsQuery.data,
    assetsChartOfAccountsQuery.data,
    revenueMethodsQuery.data,
    banksQuery.data,
    campaignsQuery.data
  ]);

  const onSubmit = values => {
    const revenueData = {
      chart_of_account_id: values.chart_of_account_id ?? "",
      is_pledge: Number(values.is_pledge) ?? 0,
      campaign_id: values.campaign_id ?? "",
      amount: values.amount ?? "",
      description: values.description ?? "",
      receipt_number: values.receiptNumber ?? "",
      cheque_number: values.chequeNumber ?? "",
      account_holder_name: values.accountHolderName ?? "",
      account_number: values.accountNumber ?? "",
      phone_number: values.phoneNumber ?? "",
      received_date: values.receivedDate ?? "",
      cheque_due_date: values.dueDate ?? "",
      cash_received_date: values.cashReceivedDate ?? "",
      transaction_number: values.referenceNumber ?? "",
      reference_number: values.referenceNumber ?? "",
      bank_id: values.bank_id ?? "",
      bank_name: values.bankName ?? "",
      revenue_method_id: values.method ?? "",
      church_id: selectedChurch?.id,
      member_ids: isMember ? selectedMembers.map(member => member.id) : [],
      partner_ids: !isMember ? selectedPartners.map(partner => partner.id) : [],
      cash_chart_of_account_id: values.cash_chart_of_account_id ?? ""
    };

    if (selectedFile) {
      revenueData["bank_slip_attachment"] = selectedFile;
    }

    const revenueFormData = convertObjectToFormData(revenueData);

    updateRevenueMutation.mutate(
      {
        churchId: selectedChurch?.id,
        revenueId: data?.id,
        revenueData: revenueFormData
      },
      {
        onSuccess: () => {
          setIsMember(true);
          setSelectedMembers([]);
          setSelectedPartners([]);
          setSelectedFile(null);
          closeUpdateRevenueModal();
        }
      }
    );
  };

  if (
    membersQuery.isLoading ||
    partnersQuery.isLoading ||
    chartOfAccountsQuery.isLoading ||
    assetsChartOfAccountsQuery.isLoading ||
    revenueMethodsQuery.isLoading ||
    banksQuery.isLoading ||
    campaignsQuery.isLoading
  ) {
    return (
      <BaseModal
        title="Recorded Revenue"
        isOpen={updateRevenueModalOpen}
        onClose={closeUpdateRevenueModal}
      >
        <Stack alignItems="center" justifyContent="center" flex={1}>
          <Loading />
        </Stack>
      </BaseModal>
    );
  }

  const isVoided = data ? Boolean(data.is_voided) : false;

  return (
    <BaseModal
      title="Recorded Revenue"
      width="800px"
      isOpen={updateRevenueModalOpen}
      onClose={closeUpdateRevenueModal}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Button
            flavor="neutral"
            size="small"
            onClick={() => {
              openReveneVoidModal();
              closeUpdateRevenueModal();
            }}
            disabled={updateRevenueMutation.isLoading || isVoided}
          >
            Void
          </Button>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              flavor="white"
              size="small"
              onClick={closeUpdateRevenueModal}
              disabled={updateRevenueMutation.isLoading}
            >
              Cancel
            </Button>

            <Button
              size="small"
              onClick={handleSubmit(onSubmit)}
              disabled={updateRevenueMutation.isLoading || isVoided}
            >
              Update
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Field>
        <RadioGroup
          row
          name="memberType"
          value={isMember}
          onChange={e => {
            const newIsMember = e.target.value === "true";
            setIsMember(newIsMember);
            setSelectedMembers([]);
            setSelectedPartners([]);
          }}
        >
          <CheckboxLabel>
            <Radio value={true} size="small" />
            Is Member
          </CheckboxLabel>
          <CheckboxLabel>
            <Radio value={false} size="small" />
            Is Partner
          </CheckboxLabel>
        </RadioGroup>
      </Field>

      <Field>
        <Label>{isMember ? "Select Members" : "Select Partners"}</Label>
        {isMember ? (
          <Stack sx={{ width: "100%" }} spacing={1}>
            <StyledAutocomplete
              multiple
              value={selectedMembers}
              options={membersQuery.data?.result ?? []}
              getOptionLabel={member =>
                `${member.first_name} ${member.last_name}`
              }
              onChange={(_, newValue) => {
                setSelectedMembers(newValue);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search members..."
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-listbox": {
                      "& .MuiAutocomplete-option": {
                        fontSize: "12px"
                      }
                    }
                  }
                }
              }}
              size="small"
              getOptionKey={option => option.id}
            />
          </Stack>
        ) : (
          <Stack sx={{ width: "100%" }} spacing={1}>
            <StyledAutocomplete
              multiple
              value={selectedPartners}
              options={partnersQuery.data?.result ?? []}
              getOptionLabel={partner =>
                `${partner.first_name} ${partner.last_name}`
              }
              onChange={(_, newValue) => {
                setSelectedPartners(newValue);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search partners..."
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-listbox": {
                      "& .MuiAutocomplete-option": {
                        fontSize: "12px"
                      }
                    }
                  }
                }
              }}
              size="small"
              getOptionKey={option => option.id}
            />
          </Stack>
        )}
      </Field>

      <Field>
        <Label htmlFor="pledge">Pledge</Label>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CheckboxLabel>
            <input
              type="checkbox"
              {...register("is_pledge")}
              onChange={e => {
                setValue("is_pledge", e.target.checked);
                if (!e.target.checked) {
                  setValue("campaign_id", "");
                }
              }}
            />
            Is Pledge
          </CheckboxLabel>
          {watch("is_pledge") && (
            <Field>
              <Select
                id="campaign_id"
                {...register("campaign_id")}
                onChange={e => {
                  setValue("campaign_id", e.target.value);
                  const campaignChartOfAccount =
                    campaignsQuery.data?.result?.find(
                      campaign => String(campaign.id) === String(e.target.value)
                    );
                  if (campaignChartOfAccount) {
                    setValue(
                      "chart_of_account_id",
                      String(campaignChartOfAccount.chart_of_account_id)
                    );
                  }
                }}
              >
                <option value="" disabled>
                  Select Campaign
                </option>
                {campaignsQuery.data?.result?.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.campaign_name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </Stack>
      </Field>
      <Field>
        <Label htmlFor="chart_of_account_id">Revenue type or reason</Label>
        <StyledAutocomplete
          id="chart_of_account_id"
          options={chartOfAccountsQuery.data?.result ?? []}
          getOptionLabel={option => option.account_name}
          disabled
          value={
            chartOfAccountsQuery.data?.result?.find(
              option =>
                String(option.id) === String(watch("chart_of_account_id"))
            ) || null
          }
          onChange={(_, newValue) => {
            setValue("chart_of_account_id", newValue?.id ?? "");
          }}
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase();
            return options.filter(
              option =>
                String(option.account_name)
                  .toLowerCase()
                  .includes(searchTerm) ||
                (String(option.account_number) &&
                  String(option.account_number)
                    .toLowerCase()
                    .includes(searchTerm))
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              placeholder="Select revenue type"
            />
          )}
          slotProps={{
            paper: {
              sx: {
                "& .MuiAutocomplete-listbox": {
                  "& .MuiAutocomplete-option": {
                    fontSize: "12px"
                  }
                }
              }
            }
          }}
          size="small"
          getOptionKey={option => option.id}
        />
        <ErrorMessage
          errors={errors}
          name="chart_of_account_id"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="amount">Amount</Label>
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <NumericFormat
                customInput={Input}
                id="amount"
                thousandSeparator
                allowNegative={false}
                onValueChange={values => {
                  field.onChange(values.floatValue);
                }}
                value={field.value}
                disabled
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name="amount"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>
        <Field>
          <Label htmlFor="currency">Currency</Label>
          <Select id="currency" {...register("currency")} disabled>
            <option>ETB</option>
          </Select>
          <ErrorMessage
            errors={errors}
            name="currency"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>
      </Stack>
      <Field>
        <Label htmlFor="description">Description (optional)</Label>
        <Input id="description" {...register("description")} />
        <ErrorMessage
          errors={errors}
          name="description"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="method">Method</Label>
        <Select id="method" {...register("method")} disabled>
          <option value="" disabled>
            Select method
          </option>
          {revenueMethodsQuery.data?.result?.map(revenueMethod => (
            <option key={revenueMethod.id} value={revenueMethod.id}>
              {revenueMethod.name}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="method"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      {method === "2" && (
        <Alert severity="info" sx={{ fontSize: "12px" }}>
          Cheque Selected: After Saving Please Reconcile and Approve under
          cheque reconcile menu. Recorded Revenue will not be added until
          approved.
        </Alert>
      )}
      {method === "1" && (
        <Field>
          <Label htmlFor="cash_chart_of_account_id">
            Cash Chart of Account
          </Label>
          <Select
            id="cash_chart_of_account_id"
            {...register("cash_chart_of_account_id")}
          >
            <option value="" disabled>
              Select chart of account
            </option>
            {assetsChartOfAccountsQuery.data?.result?.map(chartOfAccount => (
              <option key={chartOfAccount.id} value={chartOfAccount.id}>
                {chartOfAccount.account_name}
              </option>
            ))}
          </Select>
          <ErrorMessage
            errors={errors}
            name="cash_chart_of_account_id"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>
      )}
      {method === "1" && (
        <Field>
          <Label htmlFor="receiptNumber">Receipt Number</Label>
          <Input id="receiptNumber" {...register("receiptNumber")} />
          <ErrorMessage
            errors={errors}
            name="receiptNumber"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>
      )}
      {method === "2" && (
        <>
          <InformationText>Cheque holder information</InformationText>
          <Stack direction="row" spacing={2}>
            <Field>
              <Label htmlFor="bankName">Bank Name</Label>
              <Input id="bankName" {...register("bankName")} />
              <ErrorMessage
                errors={errors}
                name="bankName"
                render={({ message }) => <ValidationError message={message} />}
              />
            </Field>
            <Field>
              <Label htmlFor="chequeNumber">Cheque Number</Label>
              <Input id="chequeNumber" {...register("chequeNumber")} />
              <ErrorMessage
                errors={errors}
                name="chequeNumber"
                render={({ message }) => <ValidationError message={message} />}
              />
            </Field>
          </Stack>
          <Field>
            <Label htmlFor="accountHolderName">Full Name (Cheque Owner)</Label>
            <Input id="accountHolderName" {...register("accountHolderName")} />
            <ErrorMessage
              errors={errors}
              name="accountHolderName"
              render={({ message }) => <ValidationError message={message} />}
            />
          </Field>
          <Stack direction="row" spacing={2}>
            <Field>
              <Label htmlFor="accountNumber">Account Number</Label>
              <Input id="accountNumber" {...register("accountNumber")} />
              <ErrorMessage
                errors={errors}
                name="accountNumber"
                render={({ message }) => <ValidationError message={message} />}
              />
            </Field>
            <Field>
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input id="phoneNumber" {...register("phoneNumber")} />
              <ErrorMessage
                errors={errors}
                name="phoneNumber"
                render={({ message }) => <ValidationError message={message} />}
              />
            </Field>
          </Stack>
        </>
      )}
      {(method === "3" || method === "4") && (
        <>
          <Field>
            <Label htmlFor="bank_id">Bank Account</Label>
            <Select id="bank_id" {...register("bank_id")}>
              {banksQuery.data?.result?.map(bank => (
                <option key={bank.id} value={bank.id}>
                  {bank.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label htmlFor="referenceNumber">Reference Number</Label>
            <Input id="referenceNumber" {...register("referenceNumber")} />
            <ErrorMessage
              errors={errors}
              name="referenceNumber"
              render={({ message }) => <ValidationError message={message} />}
            />
          </Field>
          <Field>
            <Label>Bank slip attachment</Label>
            {!selectedFile ? (
              <>
                <DropzoneContainer {...getRootProps()}>
                  <input {...getInputProps()} />
                  <DropzoneText>Click to upload or drag and drop</DropzoneText>
                  <DropzoneSubText>PNG, JPG or PDF (max. 2 MB)</DropzoneSubText>
                </DropzoneContainer>
                {data?.bank_slip_attachment && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 1 }}
                  >
                    <Button
                      flavor="white"
                      size="small"
                      onClick={() =>
                        setAttachmentToView(data.bank_slip_attachment)
                      }
                    >
                      View attachment
                    </Button>
                  </Stack>
                )}
              </>
            ) : (
              <Stack direction="row" alignItems="center" spacing={2}>
                <PreviewThumbnail
                  src={URL.createObjectURL(selectedFile)}
                  alt="Preview"
                />
                <IconButton onClick={() => setSelectedFile(null)}>
                  <Delete />
                </IconButton>
              </Stack>
            )}

            {fileRejections.length > 0 && (
              <Alert severity="error">
                {fileRejections
                  .map(({ errors }) =>
                    errors.map(error => {
                      if (error.code === "file-too-large") {
                        return "File size is too large. Maximum size is 2 MB.";
                      }
                      if (error.code === "file-invalid-type") {
                        return "Invalid file type. Please upload PNG, JPG or PDF files only.";
                      }
                      return error.message;
                    })
                  )
                  .join(", ")}
              </Alert>
            )}
          </Field>
        </>
      )}
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="receivedDate">Received date</Label>
          <EthiopianDateField
            size="small"
            value={receivedDate}
            onChange={e => setValue("receivedDate", e.target.value)}
          />
          <ErrorMessage
            errors={errors}
            name="receivedDate"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>
      </Stack>
      {method === "2" && (
        <Stack direction="row" spacing={2}>
          <Field>
            <Label htmlFor="dueDate">Due date</Label>
            <EthiopianDateField
              size="small"
              value={dueDate}
              onChange={e => setValue("dueDate", e.target.value)}
            />
            <ErrorMessage
              errors={errors}
              name="dueDate"
              render={({ message }) => <ValidationError message={message} />}
            />
          </Field>
          <Field>
            <Label htmlFor="cashReceivedDate">Cash Received date</Label>
            <EthiopianDateField
              size="small"
              value={cashReceivedDate}
              onChange={e => setValue("cashReceivedDate", e.target.value)}
            />
            <ErrorMessage
              errors={errors}
              name="cashReceivedDate"
              render={({ message }) => <ValidationError message={message} />}
            />
          </Field>
        </Stack>
      )}

      <MutationError error={updateRevenueMutation.error} sx={{ mt: 2 }} />
      <Lightbox
        open={!!attachmentToView}
        close={() => setAttachmentToView("")}
        slides={[{ src: `${IMAGE_URL}/${attachmentToView}` }]}
      />
    </BaseModal>
  );
}
